// US-BUCKET-15 -- guardian sets a bucket's balance to a specific value.
// US-BUCKET-17 -- same command, source_code = 'initial_balance', used from
//                the ReviewBucketsScreen during new child account setup.
//
// The form shows: current balance -> new balance -> delta preview.
// A mandatory reason field must be filled before confirming.
// If the new balance would be lower than the current balance a second
// confirmation ("balance will decrease by £X") is required per AC.

import { type BucketEntry, bucketBalanceAdjust } from '@chorepop/core/client';
import { type Component, Show, createMemo, createSignal } from 'solid-js';
import { ActionScreen } from '../components/ActionScreen';

type Props = {
  sessionId: string;
  bucket: BucketEntry;
  /** 'balance_adjustment' (US-BUCKET-15) or 'initial_balance' (US-BUCKET-17). */
  sourceCode: 'balance_adjustment' | 'initial_balance';
  currency: string;
  onSaved: (newBalanceMinorUnits: number) => void;
  onCancel: () => void;
};

function formatMoney(minorUnits: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(
      minorUnits / 100,
    );
  } catch {
    return `${currency} ${(minorUnits / 100).toFixed(2)}`;
  }
}

function parseMoney(value: string): number | null {
  const clean = value.replace(/[^0-9.]/g, '');
  const f = Number.parseFloat(clean);
  if (Number.isNaN(f) || f < 0) return null;
  return Math.round(f * 100);
}

export const BucketAdjustBalanceScreen: Component<Props> = (props) => {
  const [amountStr, setAmountStr] = createSignal((props.bucket.balanceMinorUnits / 100).toFixed(2));
  const [reason, setReason] = createSignal('');
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);
  const [confirmingDecrease, setConfirmingDecrease] = createSignal(false);

  const newBalance = createMemo(() => parseMoney(amountStr()));
  const delta = createMemo(() => {
    const nb = newBalance();
    if (nb === null) return null;
    return nb - props.bucket.balanceMinorUnits;
  });

  const isDecrease = createMemo(() => {
    const d = delta();
    return d !== null && d < 0;
  });

  const canSubmit = createMemo(() => {
    const nb = newBalance();
    const r = reason().trim();
    return nb !== null && r.length > 0 && r.length <= 120;
  });

  async function doSubmit() {
    const nb = newBalance();
    if (nb === null) return;
    setBusy(true);
    setError(null);
    try {
      const result = await bucketBalanceAdjust({
        sessionId: props.sessionId,
        bucketId: props.bucket.bucketId,
        newBalanceMinorUnits: nb,
        reason: reason().trim(),
        sourceCode: props.sourceCode,
      });
      if (!result.ok) {
        setError(result.error.message);
        return;
      }
      props.onSaved(result.value.newBalanceMinorUnits);
    } finally {
      setBusy(false);
    }
  }

  async function handleSubmit() {
    if (!canSubmit()) return;
    if (isDecrease() && !confirmingDecrease()) {
      setConfirmingDecrease(true);
      return;
    }
    await doSubmit();
  }

  const title = props.sourceCode === 'initial_balance' ? 'Set starting balance' : 'Edit balance';

  return (
    <ActionScreen
      eyebrow={props.bucket.bucketName}
      title={title}
      onBack={props.onCancel}
      footer={
        <button
          type="button"
          class="primary action-screen-primary"
          onClick={handleSubmit}
          disabled={!canSubmit() || busy()}
        >
          {busy()
            ? 'Saving…'
            : confirmingDecrease()
              ? 'Confirm decrease'
              : props.sourceCode === 'initial_balance'
                ? 'Set balance'
                : 'Update balance'}
        </button>
      }
    >
      <div class="action-form">
        <Show when={props.sourceCode === 'balance_adjustment'}>
          <div class="field-group">
            <span class="field-label">Current balance</span>
            <span class="num field-value">
              {formatMoney(props.bucket.balanceMinorUnits, props.currency)}
            </span>
          </div>
        </Show>

        <label class="field-group" for="bucket-adjust-amount">
          <span class="field-label">
            {props.sourceCode === 'initial_balance' ? 'Starting balance' : 'New balance'}
          </span>
          <input
            id="bucket-adjust-amount"
            type="number"
            min="0"
            step="0.01"
            class="input num"
            value={amountStr()}
            onInput={(e) => {
              setAmountStr(e.currentTarget.value);
              setConfirmingDecrease(false);
              setError(null);
            }}
            disabled={busy()}
          />
        </label>

        <Show when={delta() !== null && delta() !== 0}>
          <div
            class={`bucket-adjust-delta ${(delta() ?? 0) > 0 ? 'bucket-adjust-delta--increase' : 'bucket-adjust-delta--decrease'}`}
          >
            {(delta() ?? 0) > 0 ? '▲' : '▼'} {formatMoney(Math.abs(delta() ?? 0), props.currency)}
            {(delta() ?? 0) > 0 ? ' increase' : ' decrease'}
          </div>
        </Show>

        <label class="field-group" for="bucket-adjust-reason">
          <span class="field-label">
            Reason <span class="field-required">*</span>
          </span>
          <input
            id="bucket-adjust-reason"
            type="text"
            class="input"
            placeholder={
              props.sourceCode === 'initial_balance'
                ? 'e.g. Initial account setup'
                : 'e.g. Correction -- entered wrong amount'
            }
            maxlength="120"
            value={reason()}
            onInput={(e) => setReason(e.currentTarget.value)}
            disabled={busy()}
          />
          <span class="field-hint">{reason().length}/120</span>
        </label>

        <Show when={error()}>
          <div class="form-error" role="alert">
            {error()}
          </div>
        </Show>

        <Show when={confirmingDecrease()}>
          <div class="form-warning" role="alert">
            This will{' '}
            <strong>
              decrease {props.bucket.bucketName} by{' '}
              {formatMoney(Math.abs(delta() ?? 0), props.currency)}
            </strong>
            . Confirm?
          </div>
        </Show>
      </div>
    </ActionScreen>
  );
};
